import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { addDays, fromIso, toIso, type SnapshotPlanet } from '@map/shared';
import type { WorldArt } from '@map/worldkit';
import { Globe } from '../globe/Globe.js';
import { loadWorldArt } from '../globe/useWorldLayout.js';
import { fetchSnapshot, fetchSnapshotDays } from './api.js';
import { healthColor } from '../styles/tokens.js';

/**
 * The universe on a day that has passed.
 *
 * Read-only on purpose, and not merely by leaving the buttons out: this view
 * draws from a snapshot rather than from the live world, so there is nothing
 * here that could write to it. You can turn the planets, go into them and look
 * at what was where — and nothing you do changes the day you are looking at,
 * or today.
 */
export function TimeTravel({ onClose }: { onClose: () => void }) {
  const range = useQuery({ queryKey: ['snapshot-days'], queryFn: fetchSnapshotDays });
  // Today is the server's, not the browser's: it is the one the days were
  // filed under, and a laptop in another zone must not ask for tomorrow.
  const today = range.data?.to ?? null;
  const [day, setDay] = useState<string | null>(null);
  const on = day ?? today;
  const [planetIndex, setPlanetIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [art, setArt] = useState<WorldArt | null>(null);

  const snapshot = useQuery({
    queryKey: ['snapshot', on],
    queryFn: () => fetchSnapshot(on as string),
    enabled: on !== null,
    // A day already looked at does not change, so it is never refetched.
    staleTime: Infinity,
  });

  const planets = snapshot.data?.planets ?? [];
  const planet: SnapshotPlanet | undefined = planets[Math.min(planetIndex, planets.length - 1)];
  const artKey = planet?.tree.planet.artPlanetKey;

  useEffect(() => {
    if (!artKey) return;
    let cancelled = false;
    loadWorldArt(artKey).then((loaded) => !cancelled && setArt(loaded));
    return () => {
      cancelled = true;
    };
  }, [artKey]);

  const from = range.data?.from ?? null;
  const step = (days: number) => {
    if (!on || !today) return;
    const next = toIso(addDays(fromIso(on), days));
    if (from && next < from) return;
    if (next > today) return;
    setDay(next);
    setSelectedId(null);
  };

  const selected = selectedId ? planet?.tree.regions[selectedId] : undefined;

  // How deep to draw: one level below whatever is open, as in the live world.
  const depth = useMemo(() => {
    if (!selected || !planet) return 0;
    let level = 0;
    for (
      let node = selected;
      node.parentId;
      node = planet.tree.regions[node.parentId] as typeof node
    ) {
      level += 1;
    }
    return Math.min(level + 1, 3);
  }, [selected, planet]);

  const label = new Date(`${on ?? today ?? '1970-01-01'}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <div className="relative h-full w-full">
      {art && planet && (
        <Globe
          art={art}
          tree={planet.tree}
          selectedId={selectedId}
          onSelect={setSelectedId}
          depth={depth}
          buildings={planet.buildings}
          tasks={planet.doneTasks}
        />
      )}

      {!planet && (
        <div className="flex h-full items-center justify-center text-sm text-text-muted">
          {snapshot.isLoading ? '…' : 'Nothing had been made by then.'}
        </div>
      )}

      <header className="pointer-events-none absolute inset-x-0 top-0 flex flex-wrap items-center gap-3 p-4">
        <div className="pointer-events-auto flex items-center gap-1 rounded-[var(--radius-md)] border border-border bg-surface-0/80 p-1 backdrop-blur">
          <button
            onClick={() => step(-1)}
            disabled={!on || (Boolean(from) && on <= (from as string))}
            className="rounded-[var(--radius-sm)] px-2 py-1 text-sm disabled:opacity-30"
            aria-label="The day before"
          >
            ‹
          </button>
          <input
            type="date"
            value={on ?? ''}
            min={from ?? undefined}
            max={today ?? undefined}
            onChange={(e) => {
              if (!e.target.value) return;
              setDay(e.target.value);
              setSelectedId(null);
            }}
            className="bg-transparent px-1 text-sm"
          />
          <button
            onClick={() => step(1)}
            disabled={!on || !today || on >= today}
            className="rounded-[var(--radius-sm)] px-2 py-1 text-sm disabled:opacity-30"
            aria-label="The day after"
          >
            ›
          </button>
        </div>

        <span className="pointer-events-none text-sm text-text-muted">
          {label}
          {on === today && ' · today, still happening'}
          {snapshot.data?.reconstructed && ' · reconstructed'}
        </span>

        {planets.length > 1 && (
          <div className="pointer-events-auto flex gap-1">
            {planets.map((p, i) => (
              <button
                key={p.planet.id}
                onClick={() => {
                  setPlanetIndex(i);
                  setSelectedId(null);
                }}
                className="rounded-[var(--radius-sm)] border px-2 py-1 text-xs"
                style={{
                  borderColor: i === planetIndex ? 'var(--accent)' : 'var(--border)',
                  color: i === planetIndex ? 'var(--text)' : 'var(--text-muted)',
                }}
              >
                {p.planet.name}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={onClose}
          className="pointer-events-auto ml-auto rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm"
        >
          Back to now
        </button>
      </header>

      {/* What is under the pointer, said in words. Nothing to edit: this day is
          over, and the panel that edits things belongs to the live world. */}
      {selected && (
        <aside className="absolute bottom-4 left-4 rounded-[var(--radius-md)] border border-border bg-surface-0/85 p-3 text-sm backdrop-blur">
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: healthColor(selected.health.combined) }}
            />
            <span>{selected.name}</span>
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {selected.openTasks} open
            {selected.overdueTasks > 0 && `, ${selected.overdueTasks} overdue`} that day
          </p>
        </aside>
      )}

      {snapshot.data?.reconstructed && (
        <p className="absolute bottom-4 right-4 max-w-xs text-right text-xs text-text-muted">
          Worked out from what is still on record. The work, the prayers and the health that
          follows from them are that day's; names, weights and marks are today's.
        </p>
      )}
    </div>
  );
}
